import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { axiosInstance } from '../../services/api'

function NotificationBell() {
  const [invitations, setInvitations] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetchInvitations()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchInvitations = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get('/invitations/pending/')
      setInvitations((response.data.invitations || []).map(inv => ({ ...inv, accepted: false })))
    } catch (err) {
      // Silently fail — bell just won't show a badge
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async (invId) => {
    try {
      await axiosInstance.post(`/invitations/${invId}/accept_in_app/`)
      setInvitations(prev => prev.map(i => i.id === invId ? { ...i, accepted: true } : i))
      // Reload so FamilySwitcher picks up the new family
      setTimeout(() => window.location.reload(), 800)
    } catch (err) {
      console.error('Error accepting invitation:', err)
    }
  }

  const handleDecline = async (invId) => {
    try {
      await axiosInstance.post(`/invitations/${invId}/decline/`)
      setInvitations(prev => prev.filter(i => i.id !== invId))
    } catch (err) {
      console.error('Error declining invitation:', err)
    }
  }

  const pendingCount = invitations.filter(i => !i.accepted).length
  const displayCount = pendingCount > 9 ? '9+' : pendingCount

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setShowDropdown(v => !v)}
        className="relative p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
        aria-label={`${pendingCount} pending notification${pendingCount !== 1 ? 's' : ''}`}
      >
        <span className="text-lg">🔔</span>
        {pendingCount > 0 && (
          <span className="absolute top-0 right-0.5 min-w-[18px] h-[18px] bg-[#E65100] border-2 border-[#556B2F] rounded-full flex items-center justify-center text-white text-[10px] font-bold">
            {displayCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full right-0 mt-1 w-[330px] bg-white rounded-xl border border-[#CCCCCC] shadow-lg overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#CCCCCC]">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[#333333]">Notifications</span>
              {pendingCount > 0 && (
                <span className="bg-[#E65100] text-white text-[10px] font-bold rounded-full px-2 py-px">
                  {pendingCount}
                </span>
              )}
            </div>
            <button
              onClick={() => setShowDropdown(false)}
              className="text-[#666666] hover:text-[#333333] text-base bg-transparent border-none cursor-pointer"
            >
              ✕
            </button>
          </div>

          {/* List */}
          <div className="max-h-[360px] overflow-y-auto">
            {invitations.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#999999]">No notifications</div>
            ) : (
              invitations.map((inv, idx) => (
                <div
                  key={inv.id}
                  className="px-4 py-3"
                  style={{
                    borderBottom: idx < invitations.length - 1 ? '1px solid #F2F2F2' : 'none',
                    background: inv.accepted ? 'rgba(85,107,47,0.04)' : '#fff',
                    borderLeft: `3px solid ${inv.accepted ? '#556B2F' : '#E65100'}`,
                  }}
                >
                  <div className="flex gap-2.5">
                    <div className="w-[34px] h-[34px] rounded-full bg-[#556B2F] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                      {inv.inviter_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#333333] leading-relaxed">
                        <strong>{inv.inviter_name}</strong> invited you to <strong>{inv.family_name}</strong> as <strong>{inv.role}</strong>
                      </div>
                      <div className="text-[11px] text-[#999999] mt-0.5">{timeAgo(inv.created_at)}</div>
                      {!inv.accepted ? (
                        <div className="flex gap-1.5 mt-2">
                          <button
                            onClick={() => handleAccept(inv.id)}
                            className="px-3 py-1 text-[11px] font-bold text-white bg-[#556B2F] rounded-md border-none cursor-pointer hover:bg-[#4A5D23] transition-colors"
                          >
                            ✓ Accept
                          </button>
                          <button
                            onClick={() => handleDecline(inv.id)}
                            className="px-3 py-1 text-[11px] font-bold text-[#666666] bg-transparent border border-[#666666] rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            Decline
                          </button>
                        </div>
                      ) : (
                        <div className="text-[11px] text-[#2d6a00] mt-1.5">✅ Joined</div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-[#CCCCCC] text-center">
            <button
              onClick={() => { setShowDropdown(false); navigate('/family-hub?tab=members'); }}
              className="text-xs text-[#556B2F] font-semibold cursor-pointer bg-transparent border-none"
            >
              View all in Family tab →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
